import type { AxiosInstance } from '@b2ag/axios-client'
import { createFrontAxiosClient } from '@b2ag/axios-client'
import { isEmpty } from '@b2ag/utils/src/isEmpty'
import {
  type ProductSDK,
  type ProduitB2AgWithoutProductsInPackApiResponse,
  type ProduitCatalogueCooperativeB2AgApiResponse,
  ProduitType,
} from '@b2ag/module-product-sdk'
import type { CooperativeProduct, FlattenedPackProduct, Product } from './product'
import { ProductRedirectError } from './product'
import { isProductRedirectUrl } from './product.business'
import { mapFromApi as mapProductFromApi } from './product.mapper'
import { mapFromApi as mapProductListFromApi } from './productList.mapper'

export const FETCH_PRODUCT_ERROR = 'FETCH_PRODUCT_ERROR'

class ProductService {
  readonly api: AxiosInstance

  constructor(
    productApiHost: string,
    private sdk: ProductSDK,
  ) {
    this.api = createFrontAxiosClient(`${productApiHost}/v1`)
  }

  async findProductId(eanCode: string): Promise<number | undefined> {
    const products = await this.sdk.getProducts({ ean_code: eanCode } as any)
    const { _id: id }: any = products[0] || {}
    return id
  }

  async findProductSlug(eanCode: string): Promise<number | undefined> {
    const products = await this.sdk.getProducts({ ean_code: eanCode } as any)
    const { slug }: any = products[0] || {}
    return slug
  }

  async get(id: string, cooperativeId?: number): Promise<CooperativeProduct> {
    let productData
    if (cooperativeId) {
      productData = await this.sdk.getDistributorCatalogProduct({ cooperativeId: String(cooperativeId), productId: id })
    } else {
      productData = await this.sdk.getProduct(id)
      if (isProductRedirectUrl(productData)) {
        throw new ProductRedirectError(productData.redirectUrl)
      }
    }
    const product = mapProductFromApi(productData as ProduitCatalogueCooperativeB2AgApiResponse)
    return ProductService.isPackProduct(product)
      ? ProductService.flattenPackProduct(product, productData!.products_in_pack!)
      : product
  }

  async getCatalog(id: string, cooperativeId: string): Promise<CooperativeProduct> {
    try {
      const response = await this.api.get<ProduitCatalogueCooperativeB2AgApiResponse>(
        `/cooperatives/${cooperativeId}/products/${id}`,
      )
      const product = mapProductFromApi(response.data)
      return ProductService.isPackProduct(product)
        ? ProductService.flattenPackProduct(product, response.data.products_in_pack!)
        : product
    } catch (_: any) {
      throw new Error(FETCH_PRODUCT_ERROR)
    }
  }

  static isPackProduct(product: CooperativeProduct): boolean {
    return product.type === ProduitType.PACK
  }

  static flattenPackProduct(
    packProduct: Product | CooperativeProduct,
    productsInPack: ProduitB2AgWithoutProductsInPackApiResponse[],
  ): FlattenedPackProduct {
    const flattenedProduct = { ...packProduct } as any as FlattenedPackProduct

    const fieldsToFlatten: Array<{ domainField: string; apiField: string }> = [
      { domainField: 'cropProtectionProductFormulationName', apiField: 'crop_protection_product_formulation_name' },
      { domainField: 'market_authorization', apiField: 'market_authorization' },
      { domainField: 'toxicities', apiField: 'toxicities' },
      { domainField: 'associatedUses', apiField: 'associated_uses' },
      { domainField: 'active_substances', apiField: 'active_substances' },
    ]

    fieldsToFlatten.forEach(({ domainField, apiField }) => {
      ;(flattenedProduct as any)[domainField] = productsInPack!.map((product) => {
        if (domainField === 'associatedUses') {
          return { id: product._id, product_name: product.name, value: (product as any)[apiField] }
        }
        return { product_name: product.name, value: (product as any)[apiField] }
      })
    })

    flattenedProduct.safetySheetUrls = productsInPack!

      .filter((product) => product.documentation?.safety_sheet_urls)
      .map((product) => ({
        product_name: product.name,

        value: product.documentation?.safety_sheet_urls,
      }))
    return flattenedProduct
  }

  async getHighlighted(cooperativeId: number): Promise<CooperativeProduct[]> {
    const response = await this.api.get<ProduitCatalogueCooperativeB2AgApiResponse[]>(
      `/cooperatives/${cooperativeId}/products`,
      { params: { highlighted: true } },
    )
    return mapProductListFromApi(response.data)
  }

  async getOffSeason(cooperativeId: number): Promise<CooperativeProduct[]> {
    const response = await this.api.get<ProduitCatalogueCooperativeB2AgApiResponse[]>(
      `/cooperatives/${cooperativeId}/products`,
      { params: { off_season: true } },
    )
    return mapProductListFromApi(response.data)
  }

  async findProducts(idList: string[], cooperativeId?: number): Promise<CooperativeProduct[]> {
    const response = await this.api.get<ProduitCatalogueCooperativeB2AgApiResponse[]>(
      cooperativeId ? `/cooperatives/${cooperativeId}/products` : '/products',
      {
        params: {
          _id: idList.join(','),
          limit: 100 /** se poser la question si on devrait récupérer autant de produits */,
        },
      },
    )
    return response.data.map((responseProduct) => {
      const product = mapProductFromApi(responseProduct)
      return ProductService.isPackProduct(product)
        ? ProductService.flattenPackProduct(product, responseProduct.products_in_pack!)
        : product
    })
  }

  isAvailable(product: Product): boolean {
    if (!product || product._id === 'ID_NOT_FOUND' || isEmpty(product.variants)) {
      return false
    }

    const isVariantAvailable = (variant: any) => !isEmpty(variant.offers)
    return product.variants.some(isVariantAvailable)
  }
}

export default ProductService
